import React, { useState, useEffect } from "react";
import axios from "axios";
import { Shield, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../i18n";

const API = "/api";

const PublicAliasConfig = ({ vehicleId }) => {
  const { t } = useI18n();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alias, setAlias] = useState("");
  const [effective, setEffective] = useState("");

  useEffect(() => {
    fetchAlias();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleId]);

  const fetchAlias = async () => {
    try {
      const res = await axios.get(`${API}/vehicles/${vehicleId}/public-alias`);
      setAlias(res.data.public_alias || "");
      setEffective(res.data.effective_display || "");
    } catch (err) {
      // non-blocking
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/vehicles/${vehicleId}/public-alias`, {
        public_alias: alias.trim(),
      });
      setEffective(res.data.effective_display || "");
      toast.success(t("owner.alias.saved"));
    } catch (err) {
      toast.error(t("owner.alias.error"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <div className="bg-white backdrop-blur-xl rounded-2xl border border-slate-200 p-6" data-testid="public-alias-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-cyan-500/20 rounded-full flex items-center justify-center">
          <Shield className="w-5 h-5 text-cyan-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-[#0B2545]" style={{ fontFamily: "Manrope, sans-serif" }}>
            {t("owner.alias.title")}
          </h3>
          <p className="text-slate-500 text-sm">
            {t("owner.alias.desc")}
          </p>
        </div>
      </div>

      {/* Privacy warning */}
      <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 mb-4">
        <AlertTriangle size={16} className="text-amber-400 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm text-amber-700 font-medium">{t("owner.alias.warn_title")}</p>
          <p className="text-xs text-amber-700/70 mt-1">
            {t("owner.alias.warn_text")}
          </p>
        </div>
      </div>

      <label className="block text-sm text-slate-600 mb-2">{t("owner.alias.label")}</label>
      <input
        type="text"
        value={alias}
        onChange={(e) => setAlias(e.target.value.slice(0, 40))}
        placeholder={t("owner.alias.placeholder")}
        maxLength={40}
        className="w-full h-11 bg-white border border-slate-200 text-[#0B2545] placeholder:text-slate-400 focus:border-cyan-400 focus:outline-none rounded-xl px-3"
        data-testid="public-alias-input"
      />

      <div className="flex items-center gap-2 mt-3 text-xs text-slate-500">
        <CheckCircle2 size={12} className="text-[#1B84FF]" />
        <span>
          {t("owner.alias.scanner_sees")}: <strong className="text-slate-700">{alias.trim() ? alias.trim() : effective}</strong>
        </span>
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        className="mt-4 w-full h-11 bg-[#1B84FF] hover:bg-[#0F6FDB] disabled:opacity-50 text-white rounded-xl font-medium flex items-center justify-center gap-2"
        data-testid="save-public-alias-btn"
      >
        {saving ? <Loader2 size={16} className="animate-spin" /> : <Shield size={16} />}
        {saving ? t("owner.profile.saving") : t("owner.alias.save_btn")}
      </button>
    </div>
  );
};

export default PublicAliasConfig;
